import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Package } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { getBundlePhoto } from "@/lib/productPhotos";

// ---------------------------------------------------------------------------
// Reconstitution Kit cross-sell. Shown only on Raw Vial product pages, the
// buyer literally has lyophilised powder that they need to reconstitute, so
// this is the natural moment to offer the supplies bundle.
// ---------------------------------------------------------------------------
export default function ReconstitutionKitCta() {
  const { addItem } = useCart();
  const { format: fmtPrice } = useCurrency();
  const { data: bundles = [] } = useQuery({
    queryKey: ["bundles"],
    queryFn: async () => (await api.get("/bundles")).data,
  });
  const kit = bundles.find((b) => b.slug === "reconstitution-kit");
  if (!kit) return null;

  const add = () => {
    addItem({
      id: kit.id,
      slug: kit.slug,
      name: kit.name,
      price: kit.price,
      image: kit.image,
    });
  };

  return (
    <section className="mt-16 overflow-hidden rounded-2xl panel-warm bg-white" data-testid="reconstitution-kit-cta">
      {/*
        Column split rationale:
          • The bundle photo is a native 3:2 crop (1350x900).
          • For the image cell to end up at exactly 3:2 without any
            crop or letterbox band, we need:
              image_col_width / row_height  =  3 / 2
            The row height is determined by the text panel's natural
            content height at its column width. Splitting the grid as
            `1fr 1.5fr` sets image_col_width = 1.5 × text_col_width.
            The text panel's aspect (text_col_width : row_height) is
            close to 1:1 for the current copy at desktop breakpoints,
            so the image column lands at 1.5× row height ≈ 3:2.
          • No individual text element is resized. The description
            paragraph and headline wrap naturally at the narrower text
            column, but font sizes, checklist rows, price line, and
            both CTA buttons stay at their existing intrinsic sizes.
          • Below md the layout collapses to single-column and the
            image cell hides, unchanged mobile behaviour.
      */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr]">
        <div className="p-8 md:py-4">
          <p className="label-mono text-brandblue">↳ Add on before checkout</p>
          <h3 className="mt-3 font-heading text-2xl font-black text-ink sm:text-3xl">
            Laboratory preparation supplies
          </h3>
          <p className="mt-3 text-sm text-subtle">
            Raw vial research material requires appropriate laboratory preparation equipment.
            The available kit groups compatible preparation and handling supplies in one bundle.
          </p>
          <ul className="mt-5 space-y-2 text-sm text-ink" data-testid="recon-kit-items">
            {(kit.items || []).map((line) => (
              <li key={line} className="flex items-start gap-2">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-brandblue" strokeWidth={2.5} />
                <span>{line}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-heading text-3xl font-black text-ink" data-testid="recon-kit-price">
              {fmtPrice(kit.price, { fractionDigits: 2 })}
            </span>
            <span className="text-sm text-subtle line-through">
              {fmtPrice(kit.original_price, { fractionDigits: 2 })}
            </span>
            <span className="rounded-full bg-brandblue/10 px-2.5 py-0.5 font-mono text-[10px] font-bold tracking-[0.1em] text-brandblue">
              SAVE {kit.save_percent}%
            </span>
          </div>
          <div className="mt-5 flex flex-wrap gap-3">
            <button
              onClick={add}
              className="btn-pill bg-navy px-6 py-3 text-sm font-semibold text-white hover:bg-navydark"
              data-testid="add-recon-kit"
            >
              Add kit to cart →
            </button>
            <Link
              to="/accessories"
              className="btn-pill btn-outline px-6 py-3 text-sm font-semibold"
              data-testid="browse-accessories"
            >
              Buy pieces separately
            </Link>
          </div>
        </div>
        <div className="hidden bg-[#F6F7F9] md:block" data-testid="recon-kit-cta-image">
          {(() => {
            // Cell width and height are set by the parent grid + text
            // panel padding so the cell resolves to exactly 3:2 at the
            // current row height (728.4 x 485.6 = 1.500 in production).
            // With aspect match, object-contain and object-cover render
            // identically, zero crop, zero letterbox band. object-
            // contain is used defensively so a future text-copy change
            // that grows the panel by a few px will letterbox rather
            // than silently crop the image.
            const photo = getBundlePhoto("reconstitution-kit");
            if (photo) {
              return (
                <img
                  src={photo}
                  alt={kit.name}
                  className="h-full w-full object-contain object-center"
                  // Renders inside the PDP "Reconstitution kit" CTA
                  // which is always below the fold on mobile and desktop.
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                />
              );
            }
            return (
              <div className="flex h-full w-full items-center justify-center">
                <Package className="h-32 w-32 text-navy" strokeWidth={1.2} />
              </div>
            );
          })()}
        </div>
      </div>
    </section>
  );
}
